import React, { useCallback, useRef, useState } from 'react'
import { Grid } from '@material-ui/core'
import Loading from '../../../molecules/Loading'
import EmptySection from '../../../atoms/EmptySection'
import SydButton from '../../../commonDesign/Button'
import FlexRow from '../../../molecules/FlexRow'
import FlexActions from '../../../molecules/FlexActions'
import SydModal from '../../../commonDesign/SydModal'
import SydModalActions from '../../../commonDesign/SydModal/SydModalActions'
import {
  useListDelegatedIdentities,
  useEnableDelegatedIdentity,
  useDisableDelegatedIdentity,
  useDeleteDelegatedIdentity
} from '../../../../api/delegatedIdentities'
import { decipherError } from '../../../../utils/decipherError'
import DelegatedIdentityTable from './DelegatedIdentityTable'
import DelegatedIdentityDialog from './DelegatedIdentityDialog'

function DelegatedIdentityList () {
  const { data: identities, isLoading } = useListDelegatedIdentities()
  const { mutateAsync: enableIdentity } = useEnableDelegatedIdentity()
  const { mutateAsync: disableIdentity } = useDisableDelegatedIdentity()
  const { mutateAsync: deleteIdentity } = useDeleteDelegatedIdentity()

  const dialogRef = useRef(null)

  const [confirmTarget, setConfirmTarget] = useState(null)
  const [confirmAction, setConfirmAction] = useState(null)
  const [confirmLoading, setConfirmLoading] = useState(false)
  const [confirmError, setConfirmError] = useState(null)

  const handleCreate = useCallback(() => {
    dialogRef.current?.open()
  }, [])

  const handleEdit = useCallback((identity) => {
    dialogRef.current?.open(identity.delegatedIdentityId)
  }, [])

  const openConfirm = useCallback((identity, action) => {
    setConfirmTarget(identity)
    setConfirmAction(action)
  }, [])

  const closeConfirm = useCallback(() => {
    setConfirmTarget(null)
    setConfirmAction(null)
    setConfirmError(null)
  }, [])

  const handleConfirm = useCallback(async () => {
    if (!confirmTarget || !confirmAction) return
    setConfirmLoading(true)
    setConfirmError(null)
    try {
      const id = confirmTarget.delegatedIdentityId
      if (confirmAction === 'enable') await enableIdentity({ delegatedIdentityId: id })
      if (confirmAction === 'disable') await disableIdentity({ delegatedIdentityId: id })
      if (confirmAction === 'delete') await deleteIdentity({ delegatedIdentityId: id })
      closeConfirm()
    } catch (err) {
      setConfirmError(decipherError(err))
    } finally {
      setConfirmLoading(false)
    }
  }, [confirmTarget, confirmAction, enableIdentity, disableIdentity, deleteIdentity, closeConfirm])

  if (isLoading) {
    return <Loading />
  }

  const confirmTitle = confirmAction === 'delete'
    ? 'Delete Delegated Identity'
    : confirmAction === 'enable'
      ? 'Enable Delegated Identity'
      : 'Disable Delegated Identity'

  const confirmMessage = confirmAction === 'delete'
    ? `Are you sure you want to delete "${confirmTarget?.displayName}"? This cannot be undone.`
    : `Are you sure you want to ${confirmAction} "${confirmTarget?.displayName}"?`

  return (
    <>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <FlexRow style={{ width: '100%' }}>
            <h2 style={{ margin: 0 }}>Delegated Identities</h2>
            <FlexActions style={{ flex: 1 }}>
              <SydButton
                size='sm'
                variant='primary'
                icon='add'
                onClick={handleCreate}
              >
                New Identity
              </SydButton>
            </FlexActions>
          </FlexRow>
        </Grid>
        <Grid item xs={12}>
          {!identities?.length ? (
            <EmptySection
              title='No Delegated Identities'
              description='Create a delegated identity to allow schedules and workflows to run as a specific user.'
            />
          ) : (
            <DelegatedIdentityTable
              identities={identities}
              onEnable={(id) => openConfirm(id, 'enable')}
              onDisable={(id) => openConfirm(id, 'disable')}
              onEdit={handleEdit}
              onDelete={(id) => openConfirm(id, 'delete')}
            />
          )}
        </Grid>
      </Grid>

      <DelegatedIdentityDialog ref={dialogRef} />

      <SydModal
        open={!!confirmTarget}
        onClose={closeConfirm}
        title={confirmTitle}
      >
        <p>{confirmMessage}</p>
        {confirmError ? <p style={{ color: 'var(--error)', marginTop: 8 }}>{confirmError}</p> : null}
        <SydModalActions>
          <SydButton disabled={confirmLoading} variant='ghost' size='md' onClick={closeConfirm}>
            Cancel
          </SydButton>
          <SydButton
            processing={confirmLoading}
            variant='primary'
            size='md'
            onClick={handleConfirm}
          >
            Confirm
          </SydButton>
        </SydModalActions>
      </SydModal>
    </>
  )
}

export default DelegatedIdentityList
